/**
 * GA4 Centralized Analytics System for Tykkit Customer Application.
 *
 * CRITICAL DIRECTIVES:
 * 1. Non-blocking & safe: Analytics errors will NEVER break core user flows.
 * 2. Strict PII & credential protection: Never sends name, email, phone, card numbers, OTP, or tokens.
 * 3. Idempotent Purchase tracking: Guarantees transaction_id purchase events fire AT MOST once.
 * 4. Standardized parameter naming convention (snake_case).
 * 5. event_id / ticket_id must be platform UUIDs (never slugs) for admin & organizer filtering.
 */

import {
  resolveEventId,
  resolveTicketId,
  withEventContext,
  withTicketContext,
  buildEventItem,
  buildTicketItem,
  isUuid,
} from "./helpers";

export { resolveEventId, resolveTicketId, isUuid } from "./helpers";

const trackedPurchases = new Set();

/**
 * Returns true when running on localhost with analytics force-enabled.
 * Injects debug_mode into GA4 events so DebugView registers the device.
 */
function isDebugMode() {
  if (typeof window === "undefined") return false;
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  return isLocalhost && process.env.NEXT_PUBLIC_FORCE_ANALYTICS === "true";
}

function isPurchaseTracked(transactionId) {
  if (!transactionId) return false;
  const strId = String(transactionId);
  if (trackedPurchases.has(strId)) return true;
  if (typeof window !== "undefined" && window.sessionStorage) {
    try {
      const stored = sessionStorage.getItem(`ga_purchase_${strId}`);
      if (stored) {
        trackedPurchases.add(strId);
        return true;
      }
    } catch {
      // Ignore storage errors
    }
  }
  return false;
}

function markPurchaseTracked(transactionId) {
  if (!transactionId) return;
  const strId = String(transactionId);
  trackedPurchases.add(strId);
  if (typeof window !== "undefined" && window.sessionStorage) {
    try {
      sessionStorage.setItem(`ga_purchase_${strId}`, "1");
    } catch {
      // Ignore storage errors
    }
  }
}

/**
 * Safely dispatches a GA4 event via window.gtag / dataLayer.
 */
export function safeGtag(eventName, params = {}) {
  if (typeof window === "undefined") return;

  // Initialize dataLayer and gtag if missing
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }

  try {
    const PII_KEYS = new Set([
      "email",
      "phone",
      "name",
      "first_name",
      "last_name",
      "customer_email",
      "customer_phone",
      "customer_name",
      "attendee_name",
      "otp",
      "password",
      "card",
      "cvv",
      "upi",
      "address",
      "full_address",
      "token",
      "auth_token",
    ]);

    const sanitizedParams = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") {
        if (PII_KEYS.has(key.toLowerCase())) {
          continue;
        }
        sanitizedParams[key] = value;
      }
    }

    // Inject debug_mode on localhost so GA4 DebugView registers this device
    if (isDebugMode()) {
      sanitizedParams.debug_mode = true;
    }

    window.gtag("event", eventName, sanitizedParams);
  } catch (error) {
    console.error("[GA4 Analytics Error]", error);
  }
}

/**
 * GA4 needs page_location as a full URL: it reads utm_source / utm_medium /
 * utm_campaign from it to attribute the session to a campaign. The SPA tracker
 * passes a path ("/events/x?utm_source=…"), which GA can't attribute, so the
 * visit landed under "(direct)" / "(not set)" instead of the campaign.
 */
function absolutePageUrl(url) {
  if (typeof window === "undefined") return url || "";
  if (!url) return window.location.href;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return window.location.href;
  }
}

/**
 * 1. Page View Event
 */
export function trackPageView({ url, title, page_type }) {
  safeGtag("page_view", {
    page_location: absolutePageUrl(url),
    page_title: title || (typeof window !== "undefined" ? document.title : ""),
    page_type: page_type || undefined,
  });
}

/**
 * 2. Event Listing (view_item_list)
 */
export function trackViewItemList({ items = [], item_list_name, category, city }) {
  if (!Array.isArray(items) || items.length === 0) return;

  const formattedItems = items.slice(0, 20).map((item, index) => {
    const row = buildEventItem({
      event_id: item.id,
      id: item.id,
      slug: item.slug,
      event_name: item.name || item.title,
      name: item.name || item.title,
      price: item.price ?? item.amount,
      category: item.category || category,
      city: item.city || city,
    });
    return { ...row, index: index + 1 };
  });

  safeGtag("view_item_list", {
    item_list_name: item_list_name || "Event Listing",
    event_category: category || undefined,
    event_city: city || undefined,
    items: formattedItems,
  });
}

/**
 * 3. Select Item from List (select_item)
 */
export function trackSelectItem({ item, item_list_name }) {
  if (!item) return;

  const eventId = resolveEventId(item.id, item.event_id);
  const itemRow = buildEventItem({
    event_id: eventId,
    id: item.id,
    slug: item.slug,
    event_name: item.name || item.title,
    name: item.name || item.title,
    price: item.price,
    category: item.category,
    city: item.city,
  });

  safeGtag(
    "select_item",
    withEventContext(
      {
        item_list_name: item_list_name || "Event Listing",
        items: [itemRow],
      },
      {
        event_id: eventId,
        event_name: item.name || item.title,
        city: item.city,
        category: item.category,
      },
    ),
  );
}

/**
 * 4. Event Detail View (view_item)
 */
// One event page reaches trackViewItem from more than one place:
// TrackEventView (via metaPixel.trackViewContent) and EventSummary, which also
// mounts twice for its two responsive layouts. Deduping here, per event,
// within a short window covers every caller at once. The window is short so a
// later return to the same event — a new view — still counts.
const VIEW_ITEM_DEDUPE_MS = 2000;
const recentViewItems = new Map();
function isDuplicateViewItem(eventId) {
  if (!eventId) return false;
  const now = Date.now();
  const last = recentViewItems.get(eventId);
  if (last && now - last < VIEW_ITEM_DEDUPE_MS) return true;
  recentViewItems.set(eventId, now);
  return false;
}

export function trackViewItem(eventData) {
  if (!eventData) return;

  const eventId = resolveEventId(eventData.id, eventData.event_id, eventData.eventId);
  if (isDuplicateViewItem(eventId)) return;
  const priceVal = eventData.price ?? eventData.tickets?.[0]?.price ?? 0;
  const itemRow = buildEventItem({
    event_id: eventId,
    id: eventData.id,
    slug: eventData.slug,
    event_name: eventData.name,
    name: eventData.name,
    price: priceVal,
    category: eventData.category_details?.name || eventData.category,
    city: eventData.address?.city || eventData.venue?.city,
  });

  safeGtag(
    "view_item",
    withEventContext(
      {
        currency: eventData.currency || "INR",
        value: Number(priceVal),
        event_venue:
          eventData.venue?.venue_name ||
          eventData.venue?.name ||
          eventData.address?.name ||
          undefined,
        organizer_id: eventData.organizer_id
          ? String(eventData.organizer_id)
          : undefined,
        items: [itemRow],
      },
      {
        event_id: eventId,
        event_name: eventData.name,
        start_date: eventData.start_date,
        city: eventData.address?.city || eventData.venue?.city,
        category: eventData.category_details?.name || eventData.category,
      },
    ),
  );
}

/**
 * 5. Search (search)
 */
export function trackSearch({ search_term, results_count }) {
  if (!search_term) return;

  safeGtag("search", {
    search_term: String(search_term).trim(),
    results_count: results_count !== undefined ? Number(results_count) : 0,
  });
}

/**
 * 6. Event Filter Interactions (filter_events)
 */
export function trackFilterEvents({ filter_type, filter_value, city, category }) {
  safeGtag("filter_events", {
    filter_type,
    filter_value: Array.isArray(filter_value) ? filter_value.join(",") : String(filter_value),
    event_city: city || undefined,
    event_category: category || undefined,
  });
}

/**
 * 7. Date Selection (select_event_date)
 */
export function trackSelectEventDate({ event_id, event_name, event_date, city, venue }) {
  safeGtag(
    "select_event_date",
    withEventContext(
      {
        event_date: event_date || undefined,
        event_venue: venue || undefined,
      },
      { event_id, event_name, city },
    ),
  );
}

/**
 * 8. Ticket Selection (select_ticket / select_item)
 */
export function trackSelectTicket({
  event_id,
  event_name,
  ticket_id,
  ticket_name,
  ticket_type,
  price,
  quantity,
  currency,
}) {
  const qty = Number(quantity || 1);
  const ticketPrice = Number(price || 0);
  const eventId = resolveEventId(event_id);
  const ticketId = resolveTicketId(ticket_id);
  const itemRow = buildTicketItem({
    ticket_id: ticketId,
    ticket_name,
    event_id: eventId,
    price: ticketPrice,
    quantity: qty,
    ticket_type,
  });

  safeGtag(
    "select_item",
    withTicketContext(
      withEventContext(
        {
          quantity: qty,
          price: ticketPrice,
          currency: currency || "INR",
          value: qty * ticketPrice,
          items: [itemRow],
        },
        { event_id: eventId, event_name },
      ),
      { ticket_id: ticketId, ticket_name, ticket_type },
    ),
  );
}

/**
 * 8b. Book Now Clicked (book_now_clicked)
 */
export function trackBookNowClick({ event_id, event_name, event_city, event_date }) {
  const eventId = resolveEventId(event_id);
  const itemRow = buildEventItem({
    event_id: eventId,
    event_name,
  });

  safeGtag(
    "book_now_clicked",
    withEventContext(
      {
        items: eventId || event_name ? [itemRow] : undefined,
      },
      { event_id: eventId, event_name, city: event_city, start_date: event_date },
    ),
  );
}

/**
 * 8c. Booking Page Viewed (booking_page_viewed)
 */
export function trackBookingPageView({ event_id, event_name, event_city }) {
  const eventId = resolveEventId(event_id);
  const itemRow = buildEventItem({ event_id: eventId, event_name });

  safeGtag(
    "booking_page_viewed",
    withEventContext(
      {
        items: eventId || event_name ? [itemRow] : undefined,
      },
      { event_id: eventId, event_name, city: event_city },
    ),
  );
}

/**
 * 8d. Ticket Quantity Updated (ticket_quantity_updated)
 */
export function trackTicketQuantityUpdated({
  event_id,
  event_name,
  ticket_id,
  ticket_name,
  quantity,
  price,
  currency,
}) {
  const qty = Number(quantity || 1);
  const ticketPrice = Number(price || 0);
  const eventId = resolveEventId(event_id);
  const ticketId = resolveTicketId(ticket_id);
  const itemRow = buildTicketItem({
    ticket_id: ticketId,
    ticket_name,
    event_id: eventId,
    price: ticketPrice,
    quantity: qty,
  });

  safeGtag(
    "ticket_quantity_updated",
    withTicketContext(
      withEventContext(
        {
          quantity: qty,
          price: ticketPrice,
          currency: currency || "INR",
          value: qty * ticketPrice,
          items: [itemRow],
        },
        { event_id: eventId, event_name },
      ),
      { ticket_id: ticketId, ticket_name },
    ),
  );
}

/**
 * Add to cart — includes event_id + ticket_id for organizer/admin ticket analytics.
 */
export function trackAddToCart({
  event_id,
  event_name,
  ticket_id,
  ticket_name,
  quantity = 1,
  price = 0,
  currency = "INR",
}) {
  const qty = Number(quantity) || 1;
  const unitPrice = Number(price) || 0;
  const totalValue = qty * unitPrice;
  const eventId = resolveEventId(event_id);
  const ticketId = resolveTicketId(ticket_id);
  const itemRow = buildTicketItem({
    ticket_id: ticketId,
    ticket_name,
    event_id: eventId,
    price: unitPrice,
    quantity: qty,
  });

  safeGtag(
    "add_to_cart",
    withTicketContext(
      withEventContext(
        {
          currency: currency || "INR",
          value: totalValue,
          items: [itemRow],
        },
        { event_id: eventId, event_name },
      ),
      { ticket_id: ticketId, ticket_name },
    ),
  );
}

/**
 * 9. Begin Checkout (begin_checkout)
 */
export function trackBeginCheckout({
  event_id,
  event_name,
  ticket_id,
  ticket_name,
  ticket_type,
  quantity,
  value,
  currency,
  coupon,
}) {
  const qty = Number(quantity || 1);
  const totalVal = Number(value || 0);
  const eventId = resolveEventId(event_id);
  const ticketId = resolveTicketId(ticket_id);
  const itemRow = buildTicketItem({
    ticket_id: ticketId,
    ticket_name,
    event_id: eventId,
    price: qty > 0 ? totalVal / qty : totalVal,
    quantity: qty,
    ticket_type,
  });
  if (event_name && !itemRow.item_name) itemRow.item_name = event_name;

  safeGtag(
    "begin_checkout",
    withTicketContext(
      withEventContext(
        {
          quantity: qty,
          value: totalVal,
          currency: currency || "INR",
          coupon: coupon || undefined,
          items: [itemRow],
        },
        { event_id: eventId, event_name },
      ),
      { ticket_id: ticketId, ticket_name, ticket_type },
    ),
  );
}

/**
 * 10. Add Attendee Info (add_attendee_info)
 * Sends only non-sensitive counts and event context.
 */
export function trackAddAttendeeInfo({ event_id, event_name, ticket_count, value, currency }) {
  const eventId = resolveEventId(event_id);
  safeGtag(
    "add_attendee_info",
    withEventContext(
      {
        quantity: Number(ticket_count || 1),
        value: Number(value || 0),
        currency: currency || "INR",
        items: eventId || event_name
          ? [buildEventItem({ event_id: eventId, event_name })]
          : undefined,
      },
      { event_id: eventId, event_name },
    ),
  );
}

/**
 * 11. Payment Started (payment_started)
 */
export function trackPaymentStarted({ event_id, event_name, value, currency, payment_method = "razorpay" }) {
  const eventId = resolveEventId(event_id);
  safeGtag(
    "payment_started",
    withEventContext(
      {
        value: Number(value || 0),
        currency: currency || "INR",
        payment_method,
        items: eventId || event_name
          ? [buildEventItem({ event_id: eventId, event_name })]
          : undefined,
      },
      { event_id: eventId, event_name },
    ),
  );
}

/**
 * 12. Payment Failed (payment_failed)
 */
export function trackPaymentFailed({ event_id, event_name, value, currency, failure_reason, payment_method = "razorpay" }) {
  const safeReasons = new Set([
    "payment_declined",
    "gateway_timeout",
    "user_cancelled",
    "technical_error",
    "insufficient_funds",
    "unknown",
  ]);

  const sanitizedReason = safeReasons.has(failure_reason) ? failure_reason : "unknown";

  safeGtag(
    "payment_failed",
    withEventContext(
      {
        value: Number(value || 0),
        currency: currency || "INR",
        payment_method,
        failure_reason: sanitizedReason,
        items: resolveEventId(event_id) || event_name
          ? [buildEventItem({ event_id, event_name })]
          : undefined,
      },
      { event_id, event_name },
    ),
  );
}

/**
 * 13. Purchase Conversion (purchase)
 * IDEMPOTENT: Guaranteed to fire AT MOST once per transaction_id.
 */
export function trackPurchase({
  transaction_id,
  event_id,
  event_name,
  ticket_id,
  ticket_name,
  ticket_type,
  price,
  quantity,
  value,
  currency,
  event_city,
  event_date,
  coupon,
}) {
  if (!transaction_id) return;
  if (isPurchaseTracked(transaction_id)) {
    return;
  }

  const qty = Number(quantity || 1);
  const totalVal = Number(value !== undefined ? value : (price || 0) * qty);

  const eventId = resolveEventId(event_id);
  const ticketId = resolveTicketId(ticket_id);
  const itemRow = buildTicketItem({
    ticket_id: ticketId,
    ticket_name,
    event_id: eventId,
    price: qty > 0 ? totalVal / qty : totalVal,
    quantity: qty,
    ticket_type,
  });
  if (!itemRow.item_name) itemRow.item_name = event_name || "Event Ticket";

  safeGtag(
    "purchase",
    withTicketContext(
      withEventContext(
        {
          transaction_id: String(transaction_id),
          value: totalVal,
          currency: currency || "INR",
          coupon: coupon || undefined,
          items: [itemRow],
        },
        { event_id: eventId, event_name, city: event_city, start_date: event_date },
      ),
      { ticket_id: ticketId, ticket_name, ticket_type },
    ),
  );

  markPurchaseTracked(transaction_id);
}

/**
 * 14. Event Share (share)
 */
export function trackShare({ method, content_type = "event", item_id, event_id }) {
  const eventId = resolveEventId(event_id, item_id);
  safeGtag("share", {
    method: method || "unknown",
    content_type,
    item_id: eventId || (item_id ? String(item_id) : undefined),
    ...(eventId ? { event_id: eventId } : {}),
  });
}

/**
 * 15. User Authentication: Login (login)
 */
export function trackLogin({ method = "OTP" } = {}) {
  safeGtag("login", {
    method,
  });
}

/**
 * 16. User Authentication: Sign Up (sign_up)
 */
export function trackSignUp({ method = "OTP" } = {}) {
  safeGtag("sign_up", {
    method,
  });
}

/**
 * 17. Season Pass Events
 */
export function trackSeasonPassView({ season_pass_id, season_pass_name, number_of_events, price, currency, event_id }) {
  const parentEventId = resolveEventId(event_id);
  safeGtag("season_pass_view", {
    season_pass_id: resolveTicketId(season_pass_id) || String(season_pass_id || ""),
    season_pass_name: season_pass_name || undefined,
    number_of_events: number_of_events ? Number(number_of_events) : undefined,
    price: price ? Number(price) : undefined,
    currency: currency || "INR",
    ...(parentEventId ? { event_id: parentEventId } : {}),
  });
}

export function trackSeasonPassSelect({ season_pass_id, season_pass_name, price, currency, event_id }) {
  const parentEventId = resolveEventId(event_id);
  safeGtag("season_pass_select", {
    season_pass_id: resolveTicketId(season_pass_id) || String(season_pass_id || ""),
    season_pass_name: season_pass_name || undefined,
    price: price ? Number(price) : undefined,
    currency: currency || "INR",
    ...(parentEventId ? { event_id: parentEventId } : {}),
  });
}

export function trackSeasonPassPurchase({ transaction_id, season_pass_id, season_pass_name, price, currency }) {
  if (!transaction_id || isPurchaseTracked(`sp_${transaction_id}`)) return;

  safeGtag("season_pass_purchase", {
    transaction_id: String(transaction_id),
    season_pass_id: String(season_pass_id || ""),
    season_pass_name: season_pass_name || undefined,
    value: Number(price || 0),
    currency: currency || "INR",
  });

  markPurchaseTracked(`sp_${transaction_id}`);
}

/**
 * 18. Artist Page View (artist_view)
 */
export function trackArtistView({ artist_id, artist_name, genre }) {
  const id = isUuid(artist_id) ? String(artist_id) : undefined;
  safeGtag("artist_view", {
    ...(id ? { artist_id: id } : {}),
    artist_name: artist_name || undefined,
    genre: genre || undefined,
  });
}

/**
 * 19. Venue Page View (venue_view)
 */
export function trackVenueView({ venue_id, venue_name, city }) {
  const id = isUuid(venue_id) ? String(venue_id) : undefined;
  safeGtag("venue_view", {
    ...(id ? { venue_id: id } : {}),
    venue_name: venue_name || undefined,
    event_city: city || undefined,
  });
}
